<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, toValue, watch } from 'vue'
import { Icon } from '@iconify/vue'
import { isStaging } from '@/modules/firebase'
import {
    getLittaApiOverrideUrl,
    getLittaPublicApiBaseUrl,
    isLittaLocalEnabled,
    isTrustedOverrideUrl,
    setLittaApiOverrideUrl,
    setLittaLocalEnabled,
} from '@/lib/api/commercial-accounts/litta-api-fetchers'
import { devToolbarEntries } from '@/composables/useDevToolbarEntry'

const PILL_SIZE = 48
const PANEL_WIDTH = 272
const SNAP_GAP = 12
const DRAG_THRESHOLD = 4
const STORAGE_KEY = 'devToolbar:position'
const RECENTS_KEY = 'littaApi:recentOverrides'
// Idle previews scale to zero and can take tens of seconds to cold start
const HEALTH_TIMEOUT_MS = 45000

const isOpen = ref(false)
const isHidden = ref(false)
const useLittaLocal = ref(isLittaLocalEnabled())

const toggleLittaLocal = () => {
    useLittaLocal.value = !useLittaLocal.value
    setLittaLocalEnabled(useLittaLocal.value)
}

const channelMatch = window.location.hostname.match(
    /^beehive-litta-co--(.+)\.web\.app$/,
)
const badgeText = channelMatch ? channelMatch[1] : 'STAGING'

// Litta API override
const overrideUrl = ref(getLittaApiOverrideUrl())
const overrideInput = ref(overrideUrl.value ?? '')
const overrideError = ref<string | null>(null)
const overrideChecking = ref(false)
const overrideHealth = ref<'ok' | 'fail' | null>(null)

const overrideHost = computed(() =>
    overrideUrl.value ? hostOf(overrideUrl.value) : null,
)

const effectiveApiHost = computed(() => {
    void useLittaLocal.value
    void overrideUrl.value
    return hostOf(getLittaPublicApiBaseUrl())
})

function hostOf(url: string): string {
    try {
        return new URL(url).host
    } catch {
        return url
    }
}

function normaliseUrl(raw: string): string | null {
    const trimmed = raw.trim().replace(/\/+$/, '')
    if (!trimmed) return null
    try {
        new URL(trimmed)
        return trimmed
    } catch {
        return null
    }
}

async function checkHealth(url: string): Promise<boolean> {
    try {
        const res = await fetch(`${url}/health`, {
            signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
        })
        return res.ok
    } catch {
        return false
    }
}

function loadRecentOverrides(): string[] {
    try {
        const parsed = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]')
        return Array.isArray(parsed)
            ? parsed.filter((v): v is string => typeof v === 'string')
            : []
    } catch {
        return []
    }
}

const recentOverrides = ref<string[]>(loadRecentOverrides())

function rememberOverride(url: string) {
    const next = [url, ...recentOverrides.value.filter(u => u !== url)].slice(
        0,
        5,
    )
    recentOverrides.value = next
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
}

// Reload so every module, including ones that resolved the URL at startup,
// picks up the new target consistently.
const applyOverride = async (raw: string = overrideInput.value) => {
    overrideError.value = null
    const url = normaliseUrl(raw)
    if (!url) {
        if (!raw.trim()) {
            clearOverride()
            return
        }
        overrideError.value = 'Enter a valid URL'
        return
    }
    if (!isTrustedOverrideUrl(url)) {
        overrideError.value =
            'Only litta-api Cloud Run preview URLs or localhost are allowed, to keep the API token off untrusted origins.'
        return
    }
    overrideChecking.value = true
    const healthy = await checkHealth(url)
    overrideChecking.value = false
    if (!healthy) {
        overrideError.value =
            'No response from /health within 45s. The preview may be torn down, or still cold starting: try Set again.'
        return
    }
    rememberOverride(url)
    setLittaApiOverrideUrl(url)
    window.location.reload()
}

const applyRecentOverride = (url: string) => {
    overrideInput.value = url
    void applyOverride(url)
}

const clearOverride = () => {
    setLittaApiOverrideUrl(null)
    window.location.reload()
}

// Pill colour: amber whenever anything is diverted from the deployed default
const hasActiveRegistryToggle = computed(() =>
    devToolbarEntries.value.some(
        e => e.toggle !== undefined && toValue(e.toggle),
    ),
)
const isLocalMode = computed(
    () =>
        Boolean(overrideUrl.value) ||
        useLittaLocal.value ||
        hasActiveRegistryToggle.value,
)

// Dragging
const position = ref({ x: 0, y: 0 })
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const dragStart = ref({ x: 0, y: 0 })
const hasDragged = ref(false)

// Which edge the pill is snapped to
type SnapEdge = 'left' | 'right' | 'top' | 'bottom'

const snappedEdge = computed<SnapEdge>(() => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const cx = position.value.x + PILL_SIZE / 2
    const cy = position.value.y + PILL_SIZE / 2

    // Distances to each edge
    const dLeft = cx
    const dRight = vw - cx
    const dTop = cy
    const dBottom = vh - cy

    const min = Math.min(dLeft, dRight, dTop, dBottom)
    if (min === dLeft) return 'left'
    if (min === dRight) return 'right'
    if (min === dTop) return 'top'
    return 'bottom'
})

watch(isOpen, open => {
    if (!open) return
    if (overrideUrl.value) {
        overrideHealth.value = null
        void checkHealth(overrideUrl.value).then(ok => {
            overrideHealth.value = ok ? 'ok' : 'fail'
        })
    }
})

// Panel size: auto by default, user-resizable via the native CSS handle.
// Tracked with a ResizeObserver so positioning always clamps against the
// real rendered size, whether it changed from content or from a resize.
const PANEL_SIZE_KEY = 'devToolbar:panelSize'

function loadPanelSize(): { w: number; h: number } | null {
    try {
        const parsed = JSON.parse(
            localStorage.getItem(PANEL_SIZE_KEY) ?? 'null',
        )
        return parsed &&
            typeof parsed.w === 'number' &&
            typeof parsed.h === 'number'
            ? parsed
            : null
    } catch {
        return null
    }
}

const panelEl = ref<HTMLElement | null>(null)
const savedPanelSize = ref(loadPanelSize())
const panelWidth = ref(savedPanelSize.value?.w ?? PANEL_WIDTH)
const panelHeight = ref(savedPanelSize.value?.h ?? 300)

let panelObserver: ResizeObserver | null = null

watch(
    panelEl,
    el => {
        panelObserver?.disconnect()
        panelObserver = null
        if (!el) return
        panelObserver = new ResizeObserver(() => {
            if (!panelEl.value) return
            panelWidth.value = panelEl.value.offsetWidth
            panelHeight.value = panelEl.value.offsetHeight
            // The browser sets inline width/height only when the user drags
            // the resize handle; only then is the size worth persisting.
            if (panelEl.value.style.width) {
                const size = {
                    w: panelEl.value.offsetWidth,
                    h: panelEl.value.offsetHeight,
                }
                savedPanelSize.value = size
                localStorage.setItem(PANEL_SIZE_KEY, JSON.stringify(size))
            }
        })
        panelObserver.observe(el)
    },
    { flush: 'post' },
)

onUnmounted(() => panelObserver?.disconnect())

const resetPanelSize = () => {
    localStorage.removeItem(PANEL_SIZE_KEY)
    savedPanelSize.value = null
    if (panelEl.value) {
        panelEl.value.style.width = ''
        panelEl.value.style.height = ''
    }
    panelWidth.value = PANEL_WIDTH
    panelHeight.value = 300
}

const panelSizeStyle = computed(() =>
    savedPanelSize.value
        ? {
              width: `${savedPanelSize.value.w}px`,
              height: `${savedPanelSize.value.h}px`,
          }
        : null,
)

// Panel position is always opposite the pill's edge
const panelStyle = computed(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768
    const edge = snappedEdge.value
    const height = panelHeight.value

    let left: number
    let top: number

    const width = panelWidth.value

    if (edge === 'right') {
        left = position.value.x - width - SNAP_GAP
        top = position.value.y + PILL_SIZE / 2 - height / 2
    } else if (edge === 'left') {
        left = position.value.x + PILL_SIZE + SNAP_GAP
        top = position.value.y + PILL_SIZE / 2 - height / 2
    } else if (edge === 'bottom') {
        left = position.value.x + PILL_SIZE / 2 - width / 2
        top = position.value.y - height - SNAP_GAP
    } else {
        left = position.value.x + PILL_SIZE / 2 - width / 2
        top = position.value.y + PILL_SIZE + SNAP_GAP
    }

    // Clamp to viewport
    left = Math.max(SNAP_GAP, Math.min(left, vw - width - SNAP_GAP))
    top = Math.max(SNAP_GAP, Math.min(top, vh - height - SNAP_GAP))

    return { left: `${left}px`, top: `${top}px` }
})

// Snap pill to nearest edge on release
function snapToEdge() {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const cx = position.value.x + PILL_SIZE / 2
    const cy = position.value.y + PILL_SIZE / 2

    const dLeft = cx
    const dRight = vw - cx
    const dTop = cy
    const dBottom = vh - cy
    const min = Math.min(dLeft, dRight, dTop, dBottom)

    let x = position.value.x
    let y = position.value.y

    if (min === dLeft) {
        x = SNAP_GAP
    } else if (min === dRight) {
        x = vw - PILL_SIZE - SNAP_GAP
    } else if (min === dTop) {
        y = SNAP_GAP
    } else {
        y = vh - PILL_SIZE - SNAP_GAP
    }

    // Clamp the non-snapped axis
    x = Math.max(SNAP_GAP, Math.min(x, vw - PILL_SIZE - SNAP_GAP))
    y = Math.max(SNAP_GAP, Math.min(y, vh - PILL_SIZE - SNAP_GAP))

    position.value = { x, y }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position.value))
}

onMounted(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
        try {
            position.value = JSON.parse(saved)
        } catch {
            position.value = {
                x: window.innerWidth - PILL_SIZE - SNAP_GAP,
                y: window.innerHeight - PILL_SIZE - 80,
            }
        }
    } else {
        position.value = {
            x: window.innerWidth - PILL_SIZE - SNAP_GAP,
            y: window.innerHeight - PILL_SIZE - 80,
        }
    }
    // A stored position from a larger window may be off-screen
    snapToEdge()

    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
    window.addEventListener('resize', snapToEdge)
})

onUnmounted(() => {
    document.removeEventListener('pointermove', onPointerMove)
    document.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('resize', snapToEdge)
})

const onPointerDown = (e: PointerEvent) => {
    isDragging.value = true
    hasDragged.value = false
    dragStart.value = { x: e.clientX, y: e.clientY }
    dragOffset.value = {
        x: e.clientX - position.value.x,
        y: e.clientY - position.value.y,
    }
}

const onPointerMove = (e: PointerEvent) => {
    if (!isDragging.value) return
    if (!hasDragged.value) {
        const dx = e.clientX - dragStart.value.x
        const dy = e.clientY - dragStart.value.y
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
        hasDragged.value = true
    }
    position.value = {
        x: Math.max(
            0,
            Math.min(
                window.innerWidth - PILL_SIZE,
                e.clientX - dragOffset.value.x,
            ),
        ),
        y: Math.max(
            0,
            Math.min(
                window.innerHeight - PILL_SIZE,
                e.clientY - dragOffset.value.y,
            ),
        ),
    }
}

const onPointerUp = () => {
    if (isDragging.value) {
        isDragging.value = false
        if (hasDragged.value) snapToEdge()
    }
}

const handleClick = () => {
    if (!hasDragged.value) {
        isOpen.value = !isOpen.value
    }
}

const hideUntilReload = () => {
    isOpen.value = false
    isHidden.value = true
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isStaging && !isHidden"
            class="dev-toolbar"
        >
            <!-- Floating pill button -->
            <div
                class="dev-pill"
                :class="{
                    'dev-pill--active': isOpen,
                    'dev-pill--deployed': !isLocalMode,
                    'dev-pill--emulator': isLocalMode,
                    'dev-pill--dragging': isDragging,
                }"
                :style="{ left: `${position.x}px`, top: `${position.y}px` }"
                @pointerdown="onPointerDown"
                @click="handleClick"
            >
                <Icon
                    icon="heroicons:wrench-screwdriver-20-solid"
                    class="size-5"
                />
            </div>

            <!-- Panel -->
            <Transition name="devpanel">
                <div
                    v-if="isOpen"
                    ref="panelEl"
                    class="dev-panel"
                    :style="[panelStyle, panelSizeStyle]"
                >
                    <div class="dev-panel__header">
                        <Icon
                            icon="heroicons:code-bracket-square-20-solid"
                            class="size-5 text-indigo-400"
                        />
                        <span class="dev-panel__title">Dev Tools</span>
                        <span class="dev-panel__badge">{{ badgeText }}</span>
                    </div>

                    <div class="dev-panel__body">
                        <!-- Litta API local/staging toggle -->
                        <div
                            class="dev-tool"
                            @click="toggleLittaLocal"
                        >
                            <div
                                class="dev-tool__icon"
                                :class="
                                    useLittaLocal
                                        ? 'dev-tool__icon--warn'
                                        : 'dev-tool__icon--default'
                                "
                            >
                                <Icon
                                    :icon="
                                        useLittaLocal
                                            ? 'heroicons:computer-desktop-20-solid'
                                            : 'heroicons:cloud-20-solid'
                                    "
                                    class="size-4"
                                />
                            </div>
                            <div class="dev-tool__content">
                                <span class="dev-tool__label">Litta API</span>
                                <span
                                    class="dev-tool__value"
                                    :class="
                                        useLittaLocal
                                            ? 'text-amber-400'
                                            : 'text-emerald-400'
                                    "
                                >
                                    {{
                                        overrideUrl
                                            ? 'Overridden'
                                            : useLittaLocal
                                              ? 'Local OrbStack'
                                              : 'Deployed Staging'
                                    }}
                                </span>
                            </div>
                            <div
                                class="dev-tool__toggle"
                                :class="
                                    useLittaLocal
                                        ? 'dev-tool__toggle--emulator'
                                        : 'dev-tool__toggle--deployed'
                                "
                            >
                                <div class="dev-tool__toggle-knob" />
                            </div>
                        </div>

                        <!-- Litta API preview/override URL -->
                        <div class="dev-tool dev-tool--stacked">
                            <div class="dev-tool__row">
                                <div
                                    class="dev-tool__icon"
                                    :class="
                                        overrideUrl
                                            ? 'dev-tool__icon--warn'
                                            : 'dev-tool__icon--default'
                                    "
                                >
                                    <Icon
                                        icon="heroicons:link-20-solid"
                                        class="size-4"
                                    />
                                </div>
                                <div class="dev-tool__content">
                                    <span class="dev-tool__label">
                                        Litta API Override
                                    </span>
                                    <span
                                        class="dev-tool__value"
                                        :class="
                                            overrideUrl
                                                ? 'text-amber-400'
                                                : 'text-emerald-400'
                                        "
                                    >
                                        <span
                                            v-if="overrideUrl"
                                            class="dev-tool__dot"
                                            :class="{
                                                'dev-tool__dot--ok':
                                                    overrideHealth === 'ok',
                                                'dev-tool__dot--fail':
                                                    overrideHealth === 'fail',
                                            }"
                                        />
                                        {{ overrideHost ?? 'Off' }}
                                    </span>
                                </div>
                            </div>
                            <div class="dev-tool__override">
                                <input
                                    v-model="overrideInput"
                                    class="dev-tool__input"
                                    type="url"
                                    spellcheck="false"
                                    placeholder="https://litta-api-pr-123-….run.app"
                                    @keydown.enter="applyOverride()"
                                    @pointerdown.stop
                                />
                                <button
                                    class="dev-tool__btn"
                                    :disabled="overrideChecking"
                                    @click.stop="applyOverride()"
                                >
                                    {{ overrideChecking ? '…' : 'Set' }}
                                </button>
                                <button
                                    v-if="overrideUrl"
                                    class="dev-tool__btn dev-tool__btn--clear"
                                    @click.stop="clearOverride"
                                >
                                    Clear
                                </button>
                            </div>
                            <div
                                v-if="overrideChecking"
                                class="dev-tool__hint"
                            >
                                Checking /health… a sleeping preview can take up
                                to a minute to wake.
                            </div>
                            <div
                                v-else-if="overrideError"
                                class="dev-tool__error"
                            >
                                {{ overrideError }}
                            </div>
                            <div
                                v-if="recentOverrides.length"
                                class="dev-tool__recents"
                            >
                                <button
                                    v-for="url in recentOverrides"
                                    :key="url"
                                    class="dev-tool__chip"
                                    :class="{
                                        'dev-tool__chip--active':
                                            url === overrideUrl,
                                    }"
                                    :title="url"
                                    @click.stop="applyRecentOverride(url)"
                                >
                                    {{ hostOf(url) }}
                                </button>
                            </div>
                        </div>

                        <!-- Page-specific entries registered via useDevToolbarEntry -->
                        <div
                            v-for="entry in devToolbarEntries"
                            :key="entry.id"
                            class="dev-tool"
                            @click="entry.onClick"
                        >
                            <div
                                class="dev-tool__icon"
                                :class="
                                    entry.toggle !== undefined
                                        ? toValue(entry.toggle)
                                            ? 'dev-tool__icon--warn'
                                            : 'dev-tool__icon--default'
                                        : 'dev-tool__icon--accent'
                                "
                            >
                                <Icon
                                    :icon="toValue(entry.icon)"
                                    class="size-4"
                                />
                            </div>
                            <div class="dev-tool__content">
                                <span class="dev-tool__label">
                                    {{ toValue(entry.label) }}
                                </span>
                                <span
                                    class="dev-tool__value"
                                    :class="
                                        entry.toggle !== undefined
                                            ? toValue(entry.toggle)
                                                ? 'text-amber-400'
                                                : 'text-emerald-400'
                                            : undefined
                                    "
                                >
                                    {{ toValue(entry.value) }}
                                </span>
                            </div>
                            <div
                                v-if="entry.toggle !== undefined"
                                class="dev-tool__toggle"
                                :class="
                                    toValue(entry.toggle)
                                        ? 'dev-tool__toggle--emulator'
                                        : 'dev-tool__toggle--deployed'
                                "
                            >
                                <div class="dev-tool__toggle-knob" />
                            </div>
                            <Icon
                                v-else-if="
                                    entry.loading && toValue(entry.loading)
                                "
                                icon="svg-spinners:ring-resize"
                                class="size-4 text-indigo-400"
                            />
                        </div>

                        <!-- Hide until reload -->
                        <div
                            class="dev-tool dev-tool--muted"
                            @click="hideUntilReload"
                        >
                            <div class="dev-tool__icon dev-tool__icon--muted">
                                <Icon
                                    icon="heroicons:eye-slash-20-solid"
                                    class="size-4"
                                />
                            </div>
                            <div class="dev-tool__content">
                                <span class="dev-tool__label">Dev Tools</span>
                                <span class="dev-tool__value">
                                    Hide until reload
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="dev-panel__footer">
                        <span class="dev-panel__footer-text">
                            API → {{ effectiveApiHost }}
                        </span>
                        <button
                            v-if="savedPanelSize"
                            class="dev-panel__footer-btn"
                            @click.stop="resetPanelSize"
                        >
                            Reset size
                        </button>
                    </div>
                </div>
            </Transition>

            <!-- Backdrop to close -->
            <div
                v-if="isOpen"
                class="dev-backdrop"
                @click="isOpen = false"
            />
        </div>
    </Teleport>
</template>

<style scoped>
.dev-toolbar {
    position: fixed;
    z-index: 9999;
    pointer-events: none;
}

.dev-toolbar > * {
    pointer-events: auto;
}

/* Floating pill */
.dev-pill {
    position: fixed;
    z-index: 10001;
    width: 48px;
    height: 48px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    user-select: none;
    touch-action: none;
    background: linear-gradient(135deg, #1e1b4b, #312e81);
    color: #a5b4fc;
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(99, 102, 241, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
    transition:
        transform 0.2s,
        box-shadow 0.2s,
        background 0.2s,
        left 0.3s ease-out,
        top 0.3s ease-out;
}

.dev-pill--dragging {
    transition:
        transform 0.2s,
        box-shadow 0.2s,
        background 0.2s;
    cursor: grabbing;
}

.dev-pill:hover {
    transform: scale(1.08);
    box-shadow:
        0 6px 24px rgba(0, 0, 0, 0.4),
        0 0 0 1px rgba(99, 102, 241, 0.4),
        0 0 20px rgba(99, 102, 241, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.dev-pill:active {
    transform: scale(1.04);
}

/* Spinning ring for active state */
.dev-pill--active {
    overflow: visible;
}

.dev-pill--active::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 9999px;
    padding: 2px;
    background: conic-gradient(
        from 0deg,
        transparent 0%,
        transparent 40%,
        var(--pill-glow-color, #818cf8) 50%,
        transparent 60%,
        transparent 100%
    );
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: spin-ring 1.5s linear infinite;
}

@keyframes spin-ring {
    to {
        transform: rotate(360deg);
    }
}

.dev-pill--deployed {
    --pill-glow-color: #34d399;
    background: linear-gradient(135deg, #022c22, #065f46);
    color: #34d399;
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(52, 211, 153, 0.3),
        0 0 16px rgba(52, 211, 153, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.dev-pill--deployed:hover {
    box-shadow:
        0 6px 24px rgba(0, 0, 0, 0.4),
        0 0 0 1px rgba(52, 211, 153, 0.5),
        0 0 24px rgba(52, 211, 153, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.dev-pill--emulator {
    --pill-glow-color: #fbbf24;
    background: linear-gradient(135deg, #451a03, #78350f);
    color: #fbbf24;
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(251, 191, 36, 0.3),
        0 0 16px rgba(251, 191, 36, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.dev-pill--emulator:hover {
    box-shadow:
        0 6px 24px rgba(0, 0, 0, 0.4),
        0 0 0 1px rgba(251, 191, 36, 0.5),
        0 0 24px rgba(251, 191, 36, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

/* Panel */
.dev-panel {
    position: fixed;
    z-index: 10000;
    width: 272px;
    display: flex;
    flex-direction: column;
    resize: both;
    min-width: 240px;
    min-height: 160px;
    max-width: 90vw;
    max-height: 85vh;
    border-radius: 16px;
    background: linear-gradient(180deg, #1c1c2e, #141420);
    border: 1px solid rgba(99, 102, 241, 0.15);
    box-shadow:
        0 20px 60px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.03),
        0 0 40px rgba(99, 102, 241, 0.08);
    overflow: hidden;
    backdrop-filter: blur(20px);
}

.dev-panel__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.dev-panel__title {
    font-size: 13px;
    font-weight: 600;
    color: #e0e7ff;
    letter-spacing: 0.01em;
    flex: 1;
}

.dev-panel__badge {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #818cf8;
    background: rgba(99, 102, 241, 0.12);
    padding: 2px 8px;
    border-radius: 9999px;
    border: 1px solid rgba(99, 102, 241, 0.2);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: uppercase;
}

.dev-panel__body {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.dev-panel__footer {
    padding: 8px 16px 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 10px;
    font-weight: 500;
    color: #6b7280;
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 8px;
}

.dev-panel__footer-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dev-panel__footer-btn {
    font-size: 9px;
    font-weight: 600;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 9999px;
    padding: 2px 8px;
    cursor: pointer;
    white-space: nowrap;
    transition:
        background 0.15s,
        color 0.15s;
}

.dev-panel__footer-btn:hover {
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
}

.dev-tool {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s;
}

.dev-tool:hover {
    background: rgba(255, 255, 255, 0.04);
}

.dev-tool--stacked {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    cursor: default;
}

.dev-tool--stacked:hover {
    background: transparent;
}

.dev-tool--muted {
    opacity: 0.6;
}

.dev-tool--muted:hover {
    opacity: 1;
}

.dev-tool__row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dev-tool__override {
    display: flex;
    align-items: center;
    gap: 6px;
}

.dev-tool__input {
    flex: 1;
    min-width: 0;
    font-size: 11px;
    color: #e0e7ff;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 6px 8px;
    outline: none;
    transition: border-color 0.15s;
}

.dev-tool__input:focus {
    border-color: rgba(99, 102, 241, 0.5);
}

.dev-tool__input::placeholder {
    color: #6b7280;
}

.dev-tool__btn {
    font-size: 11px;
    font-weight: 600;
    color: #a5b4fc;
    background: rgba(99, 102, 241, 0.12);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 8px;
    padding: 5px 10px;
    cursor: pointer;
    transition: background 0.15s;
}

.dev-tool__btn:hover {
    background: rgba(99, 102, 241, 0.22);
}

.dev-tool__btn:disabled {
    opacity: 0.5;
    cursor: wait;
}

.dev-tool__btn--clear {
    color: #fca5a5;
    background: rgba(248, 113, 113, 0.1);
    border-color: rgba(248, 113, 113, 0.2);
}

.dev-tool__btn--clear:hover {
    background: rgba(248, 113, 113, 0.2);
}

.dev-tool__hint {
    font-size: 10px;
    color: #9ca3af;
    line-height: 1.4;
}

.dev-tool__error {
    font-size: 10px;
    color: #fca5a5;
    line-height: 1.4;
}

.dev-tool__recents {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.dev-tool__chip {
    font-size: 10px;
    font-weight: 500;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 9999px;
    padding: 2px 8px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s;
}

.dev-tool__chip:hover {
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
}

.dev-tool__chip--active {
    color: #fbbf24;
    border-color: rgba(251, 191, 36, 0.4);
    background: rgba(251, 191, 36, 0.1);
}

.dev-tool__dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 9999px;
    margin-right: 4px;
    background: rgba(255, 255, 255, 0.25);
    vertical-align: middle;
}

.dev-tool__dot--ok {
    background: #34d399;
}

.dev-tool__dot--fail {
    background: #f87171;
}

.dev-tool__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.dev-tool__icon--default {
    background: rgba(52, 211, 153, 0.1);
    color: #34d399;
}

.dev-tool__icon--warn {
    background: rgba(251, 191, 36, 0.1);
    color: #fbbf24;
}

.dev-tool__icon--accent {
    background: rgba(99, 102, 241, 0.1);
    color: #818cf8;
}

.dev-tool__icon--muted {
    background: rgba(255, 255, 255, 0.05);
    color: #9ca3af;
}

.dev-tool__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.dev-tool__label {
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    letter-spacing: 0.02em;
}

.dev-tool__value {
    font-size: 12px;
    font-weight: 600;
    color: #a5b4fc;
    letter-spacing: 0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Toggle switch */
.dev-tool__toggle {
    width: 36px;
    height: 20px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    flex-shrink: 0;
    transition:
        background 0.2s,
        border-color 0.2s;
}

.dev-tool__toggle--deployed {
    background: rgba(52, 211, 153, 0.2);
    border-color: rgba(52, 211, 153, 0.4);
}

.dev-tool__toggle--emulator {
    background: rgba(251, 191, 36, 0.2);
    border-color: rgba(251, 191, 36, 0.4);
}

.dev-tool__toggle-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 14px;
    height: 14px;
    border-radius: 9999px;
    background: #34d399;
    transition:
        transform 0.2s,
        background 0.2s;
}

.dev-tool__toggle--emulator .dev-tool__toggle-knob {
    transform: translateX(16px);
    background: #fbbf24;
}

/* Backdrop */
.dev-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9998;
}

/* Panel transition */
.devpanel-enter-active {
    transition:
        opacity 0.15s ease-out,
        transform 0.15s ease-out;
}
.devpanel-leave-active {
    transition:
        opacity 0.1s ease-in,
        transform 0.1s ease-in;
}
.devpanel-enter-from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
}
.devpanel-leave-to {
    opacity: 0;
    transform: scale(0.97) translateY(4px);
}
</style>
